import type { AnchorHTMLAttributes, HTMLAttributes } from 'react'
import React from 'react'

import clsx from 'clsx'
import _ from 'lodash'

import { Link } from '@/shared/ui/Link'

const SHADOW_CLASS: Record<string, string> = {
  xs: 'shadow-xs',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg'
}

const cardBaseClass =
  'relative flex min-w-0 flex-col rounded-[0.875rem] border border-contour bg-white'

type CardBaseProps = {
  children?: React.ReactNode
  className?: string
  shadow?: string | number
  stretch?: boolean | string
  dashed?: boolean
  bg?: string
  flush?: boolean
  href?: string
  openLinkInNewTab?: boolean
}

type CardDivProps = CardBaseProps &
  Omit<HTMLAttributes<HTMLDivElement>, keyof CardBaseProps> & {}

type CardAnchorProps = CardBaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof CardBaseProps> & {}

type CardProps = CardDivProps | CardAnchorProps

function Card(props: CardProps) {
  const {
    children,
    shadow,
    stretch,
    dashed,
    className = '',
    bg,
    flush = false,
    href,
    openLinkInNewTab,
    ...rest
  } = props as CardProps

  const classes = clsx(
    cardBaseClass,
    {
      'h-full': stretch === true,
      'flex-1': stretch === 'stretch',
      'border-dashed': dashed,
      'overflow-hidden p-0': flush,
      'cursor-pointer': _.has(rest, 'onClick')
    },
    shadow != null && SHADOW_CLASS[String(shadow)],
    bg,
    className
  )

  if (href) {
    const anchorProps = rest as AnchorHTMLAttributes<HTMLAnchorElement>
    return (
      <Link
        href={href}
        className={classes}
        target={openLinkInNewTab ? '_blank' : '_self'}
        {...anchorProps}
      >
        {children}
      </Link>
    )
  }

  const divProps = rest as HTMLAttributes<HTMLDivElement>
  return (
    <div className={classes} {...divProps}>
      {children}
    </div>
  )
}

type SectionProps = {
  children?: React.ReactNode
  noBorder?: boolean
  className?: string
} & React.HTMLAttributes<HTMLDivElement>

function Header({ noBorder, className = '', children, ...rest }: SectionProps) {
  return (
    <div
      className={clsx(
        'flex items-center justify-between gap-3 px-4 py-3 sm:px-5',
        !noBorder && 'border-b border-contour',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

type TitleProps = {
  children?: React.ReactNode
  className?: string
} & React.HTMLAttributes<HTMLHeadingElement>

function Title({ className = '', children, ...rest }: TitleProps) {
  return (
    <h3 className={clsx('flex flex-col items-start', className)} {...rest}>
      {children}
    </h3>
  )
}

type ToolbarProps = {
  children?: React.ReactNode
  className?: string
} & React.HTMLAttributes<HTMLDivElement>

function Toolbar({ children, className = '', ...rest }: ToolbarProps) {
  return (
    <div className={clsx('my-0', className)} {...rest}>
      {children}
    </div>
  )
}

function Body({ noBorder, children, className = '', ...rest }: SectionProps) {
  return (
    <div
      className={clsx('flex-1 p-2 md:p-3', noBorder && 'border-0', className)}
      {...rest}
    >
      {children}
    </div>
  )
}

function Footer({ noBorder, children, className = '', ...rest }: SectionProps) {
  return (
    <div
      className={clsx(
        'px-4 py-3 sm:px-5',
        !noBorder && 'border-t border-contour',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  )
}

Card.Header = Header
Card.Title = Title
Card.Toolbar = Toolbar
Card.Body = Body
Card.Footer = Footer

export { Card }
